import { timestampDate } from '@bufbuild/protobuf/wkt';
import {
  useGetRole,
  useInfiniteListAuditEvents,
  useListRoleAccessRules,
} from '@sunstone/api-client/hooks';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@sunstone/ui';
import { Pencil } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { AuditHistory } from '@/components/ui/AuditHistory';
import { PageHeader } from '@/components/ui/PageHeader';
import { useAuthToken } from '@/features/auth';
import { useAuthorization } from '@/features/auth/useAuthorization';
import { AccessSummary } from '@/features/users/AccessSummary';
import { splitRulesByKind } from '@/features/users/accessRuleDraft';
import { useIsPartnerTenant, useTenantName } from '@/features/users/accessRuleSentence';
import { MemberList } from '@/features/users/MemberList';
import { permissionLabel } from '@/lib/permissions';
import { roleScopeLabel } from '@/lib/roles';
import { routes } from '@/lib/routes';
import { formatDateTime } from '@/lib/time';

type Tab = 'permissions' | 'access' | 'users' | 'history';

export function RoleDetailPage() {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const { token } = useAuthToken();
  const { isPlatformAdmin, canManageAccess, tenantId } = useAuthorization();

  const { data: roleData, isLoading } = useGetRole(roleId ?? '', token);
  const role = roleData?.role;

  const {
    data: auditData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteListAuditEvents(token, 'role', roleId);
  const auditEvents = auditData?.pages.flatMap((p) => p.events) ?? [];

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const activeTab: Tab =
    tabParam === 'history' || tabParam === 'users' || tabParam === 'access'
      ? tabParam
      : 'permissions';
  const viewTenantId = searchParams.get('tenantId') ?? '';

  const hasAccessRulesTab = role ? role.scope !== 'system' : false;
  const { data: accessRulesData } = useListRoleAccessRules(
    hasAccessRulesTab ? (roleId ?? '') : '',
    token,
  );
  const accessRules = accessRulesData?.accessRules ?? [];
  const { deployment: deploymentRules, workspace: workspaceRules } = splitRulesByKind(accessRules);
  const tenantName = useTenantName(role?.tenantId ?? '');
  const isPartner = useIsPartnerTenant(role?.tenantId ?? '');

  if (isLoading || !role) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }

  const canEdit =
    !role.isUnrestricted &&
    canManageAccess &&
    (role.scope === 'tenant' ? isPlatformAdmin || role.tenantId === tenantId : isPlatformAdmin);

  const scopeLabel = roleScopeLabel(role);
  const isPlatformUsersView = !role.tenantId && !viewTenantId;
  const usersTabTenantId = role.tenantId || viewTenantId;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <PageHeader
        title={role.name}
        description={role.description || undefined}
        action={
          canEdit ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate(routes.editRole(role.id, role.tenantId || undefined))}
            >
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardContent className="py-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Scope</p>
              <Badge variant="secondary">{scopeLabel}</Badge>
            </div>
            {role.tenantId && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 font-medium">Tenant</p>
                <p className="text-sm font-mono text-foreground truncate">{role.tenantId}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Created</p>
              <p className="text-sm text-foreground">
                {role.createdAt ? formatDateTime(timestampDate(role.createdAt)) : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Updated</p>
              <p className="text-sm text-foreground">
                {role.updatedAt ? formatDateTime(timestampDate(role.updatedAt)) : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs
        value={activeTab}
        onValueChange={(v) =>
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('tab', v);
            return next;
          })
        }
      >
        <TabsList>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
          {hasAccessRulesTab && <TabsTrigger value="access">Access Rules</TabsTrigger>}
          <TabsTrigger value="users">
            {isPlatformUsersView ? 'Platform Users' : 'Users'}
          </TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="permissions" className="space-y-6 mt-6 animate-in fade-in duration-300">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-[10px]">
                  {role.grantsAll ? 'Auto-updates with new permissions' : 'Fixed permission list'}
                </Badge>
              </div>
              {role.grantsAll ? (
                <p className="text-sm text-muted-foreground">
                  Grants every permission, including any added in the future.
                </p>
              ) : role.permissions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No permissions assigned.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {role.permissions.map((p) => (
                    <Badge key={p} variant="outline">
                      {permissionLabel(p)}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {hasAccessRulesTab && (
          <TabsContent value="access" className="space-y-6 mt-6 animate-in fade-in duration-300">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <p className="text-sm text-muted-foreground">
                  These rules apply to everyone who holds this role. A person's own access can
                  narrow further, but can never reach outside what's allowed here. Read-only here —
                  edit them from the role's Edit page.
                </p>
                <AccessSummary
                  mode="role-only"
                  tenantId={role.tenantId}
                  tenantName={tenantName}
                  isPartner={isPartner}
                  deploymentRules={deploymentRules}
                  workspaceRules={workspaceRules}
                />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="users" className="mt-6 animate-in fade-in duration-300">
          {isPlatformUsersView ? (
            <MemberList
              scope="platform_admins"
              fixedRoleId={role.id}
              onRowClick={(user) => navigate(routes.platformUserDetail(user.id))}
            />
          ) : (
            <MemberList
              scope="tenant"
              tenantId={usersTabTenantId}
              fixedRoleId={role.id}
              onRowClick={(user) => navigate(routes.userDetail(usersTabTenantId, user.id))}
            />
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-6 animate-in fade-in duration-300">
          <AuditHistory
            events={auditEvents}
            onLoadMore={fetchNextPage}
            hasMore={hasNextPage}
            isLoadingMore={isFetchingNextPage}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
